import type { NextPage } from 'next';
import Link from 'next/link';
import styled from '@emotion/styled';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useIsMobile } from '@hooks/responsive/useIsMobile';

// 다국어 지원
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// color
import { primary, blue, gray, red, white, black } from '@styles/Colors';

// components
import DefaultLayout from '@components/_templates/DefaultLayout';
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';
import RedErrorIcon from '@public/assets/3dIcons/3dRedWarning.svg';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

//constants
import { TypoVariant } from '@constants/atoms';
import { ButtonColor } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { LOGIN } from '@constants/routes/routes';
import ButtonCode from '@constants/log/buttonCode';
import { useRecoilValue } from 'recoil';
import { previousPageUrlState } from '@recoil/auth';

const ErrorWrapper = styled.div`
  width: 100%;
  max-width: 528px;
  padding: 336px 0;
  margin: 0 auto;
  text-align: center;

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 224px 0;
  }
`;

const TitleWrapper = styled.div`
  margin-top: 16px;
`;

const ContentWrapper = styled.div`
  margin: 8px auto 80px;

  @media ${MOBILE_MODE} {
    margin-bottom: 96px;
  }
`;

const PromoteSignUp: NextPage = () => {
  const { t } = useTranslation('auth');
  const isMobile = useIsMobile();
  const router = useRouter();

  const previousPageUrl = useRecoilValue(previousPageUrlState);

  const handleBack = () => {
    if (previousPageUrl) {
      router.replace(previousPageUrl);
    } else {
      router.back();
    }
  }

  return (
    <>
      <Head>
        <title>{t('promoteSignUp.meta-title')}</title>
        <meta name="title" content={t('promoteSignUp.meta-title')} />
      </Head>
      <DefaultLayout>
        <ErrorWrapper>
          <RedErrorIcon />
          <TitleWrapper>
            <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.H6} color={primary.gray}>
              {t('promoteSignUp.title')}
            </Typography>
          </TitleWrapper>
          <ContentWrapper>
            <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B2} color={gray.gray7}>
              {t('promoteSignUp.content')}
            </Typography>
          </ContentWrapper>

          <DefaultButtonBox
            button1={
              <Button
                text={t('promoteSignUp.back-button')}
                color={ButtonColor.OutlinedBlack}
                onClick={handleBack}
                steelbosoId={ButtonCode.BTN_BACK}
              />
            }
            button2={
              <Button
                text={t('promoteSignUp.login-button')}
                onClick={() => {
                  router.replace(LOGIN);
                }}
                steelbosoId={ButtonCode.BTN_LOGIN}
              />
            }
          />
        </ErrorWrapper>
      </DefaultLayout>
    </>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['auth', 'common', 'component'])),
  },
});

export default PromoteSignUp;
